import * as React from 'react';

import {
  ActivityStage,
  ActivityType,
} from '@showbie-socrative/socrative-utils';

import { Participant } from '~teacher/components/ResultsGrid/ResultsTable';
import {
  Activity,
  ApiQuestionResponse,
  QuestionScore,
  Room,
  Student,
  StudentActivityResponse,
} from '~teacher/models/activities';
import { QuizAnswer, QuizQuestion } from '~teacher/models/quizzes';

import { useNormalizedQuestion } from './useNormalizedQuestion';

export type ResultQuestionState = {
  activityInstanceId: number;
  activityType: ActivityType;
  activityStage: ActivityStage;
  totalNumQuestions: number;
  question: QuizQuestion;
  responses: StudentActivityResponse[];
  answers: QuizAnswer[];
  participants: Participant[];
  students: Record<string, Student>;
  roomName: string;
  roomHasRoster: boolean;
  numParticipantsResponded: number;
  questionScore: QuestionScore;
};

interface ProviderProps {
  children?: React.ReactNode;
  activity: Activity;
  answers: Record<string, QuizAnswer>;
  questions: Record<string, QuizQuestion>;
  responses: Record<string, ApiQuestionResponse>;
  students: Record<string, Student>;
  questionPosition?: number;
  questionScores: Record<number, QuestionScore>;
  room?: Room;
}

export const QuestionContext = React.createContext<ResultQuestionState>(null);

/**
 * A simple context hook to return question data provided by
 * `ActivityResultQuestionProvider`.
 *
 * @see ActivityResultQuestionProvider
 */
export function useResultQuestionState() {
  const context = React.useContext(QuestionContext);

  if (context === undefined) {
    throw new Error(
      'useResultQuestionState must be used within ActivityResultQuestionProvider'
    );
  }

  return context;
}

/**
 * Takes activity data and a current order in the activity for a
 * question and provides parsed and normalised Question data to any
 * child components.
 */
export function ActivityResultQuestionProvider(props: ProviderProps) {
  const { answers, question, questions, responses, participants, students } =
    useNormalizedQuestion({
      activity: props.activity,
      isRosteredRoom: props.room.isRostered,
      answers: props.answers,
      questions: props.questions,
      responses: props.responses,
      students: props.students,
      questionPosition: props.questionPosition,
      roomName: props.room?.nameLower,
      isLive: props.activity.activityStage === ActivityStage.InProgress,
      questionScores: props.questionScores,
    });
  const numParticipantsResponded = participants.reduce((acc, currentValue) => {
    const responses =
      currentValue.responsesByQuestion[question.questionId] ?? [];

    if (responses.length > 0) {
      return acc + 1;
    }

    return acc;
  }, 0);

  const questionState: ResultQuestionState = {
    activityInstanceId: props.activity.id,
    activityType: props.activity.activityType,
    activityStage: props.activity.activityStage,
    roomName: props.room.nameLower,
    roomHasRoster: props.room.isRostered,
    totalNumQuestions: questions.byOrder.length,
    questionScore: props.questionScores?.[question?.questionId],
    answers,
    question,
    responses,
    participants,
    numParticipantsResponded,
    students,
  };

  return (
    <QuestionContext.Provider value={questionState}>
      {props.children}
    </QuestionContext.Provider>
  );
}
